import React, { useEffect, useState } from 'react';
import { CSpinner, CRow, CCol, CButton, CModal, CModalHeader, CModalBody, CModalTitle } from '@coreui/react';
import { CSmartPagination } from '@coreui/react-pro';
import dayjs from 'dayjs';
import { callTokenApi } from '../../../../../../../apiCaller';
import { toast } from 'react-toastify';
import ViewMarkdownReview from '../components/ViewMarkdownReview';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useSelector } from 'react-redux';
import Accordion from '../../../../../../general/accordion';
import TooltipTag from '../../../../../../general/TooltipTag';
import { AI_INTENT_TRIGGER_TYPE } from '../../../../../../../constants/intent-rules';
import CIcon from '@coreui/icons-react';
import { cilDescription, cilCheckCircle, cilXCircle } from '@coreui/icons';

dayjs.extend(utc);
dayjs.extend(timezone);

const formatTimeRunAt = (runAt, timeZone) => (runAt ? dayjs(runAt).tz(timeZone).format('HH:mm:ss') : 'N/A');

const getTooltipTag = (triggerType) => {
     const mappingConfig = {
          [AI_INTENT_TRIGGER_TYPE.SERVER_SIDE_TRIGGER]: { name: 'Server side trigger', type: 'info' },
          [AI_INTENT_TRIGGER_TYPE.RERUN_INTENT]: { name: 'Re-run', type: 'success' },
          [AI_INTENT_TRIGGER_TYPE.MANUAL_PREVIEW]: { name: 'Preview', type: 'primary' },
     };
     const config = mappingConfig[triggerType];

     return <TooltipTag name={config.name} type={config.type} className='ms-2' />;
};

const renderStatusBadge = (status) => {
     const statusLower = status?.toLowerCase();
     const statusConfig = {
          done: { label: 'Success', className: 'bg-success', icon: cilCheckCircle },
          failed: { label: 'Failed', className: 'bg-danger', icon: cilXCircle },
     };
     const config = statusConfig[statusLower] || {
          label: status || 'Unknown',
          className: 'bg-secondary',
          icon: cilCheckCircle,
     };

     return (
          <span className={`badge ${config.className} text-white d-flex align-items-center gap-1`}>
               {config.label}
          </span>
     );
};

const RequestWrapper = ({ requests, models, timeZone, handleOpenLog, currentPage, totalPages, onPageChange }) => {
     return (
          <div className='request-wrapper d-flex flex-column'>
               {requests.map((request, idx) => {
                    const modelsName = (models || []).find((model) => model.id === request.llmModelId)?.displayName || '';

                    return (
                         <div key={request.id || idx} className='request-item d-flex align-items-center justify-content-between py-2 px-3 bg-white'>
                              <div className='d-flex align-items-center gap-2' style={{ lineHeight: '1' }}>
                                   <strong className='text-muted record-id'>Record ID: {request.objectId || 'N/A'}</strong>
                                   <span className='text-muted small'>Run time: {formatTimeRunAt(request.created_at, timeZone)}</span>
                                   <span className='text-muted small'>|</span>
                                   <span className='text-muted small'>{modelsName}</span>
                                   {getTooltipTag(request.triggerType)}
                              </div>
                              <div className='d-flex align-items-center gap-3'>
                                   {renderStatusBadge(request.status)}
                                   <CButton size='sm' onClick={() => handleOpenLog(request)} className='btn-log'>
                                        <CIcon icon={cilDescription} size='sm' />
                                        Log
                                   </CButton>
                              </div>
                         </div>
                    );
               })}
               {totalPages > 1 && (
                    <div className='d-flex justify-content-center mt-3'>
                         <CSmartPagination activePage={currentPage} pages={totalPages} onActivePageChange={onPageChange} />
                    </div>
               )}
          </div>
     );
};

const ReviewPrompt = ({ insight, ruleId, models }) => {
     const { id: accountId, timeZone } = useSelector((state) => state.subscriber.activeAccount);
     const pageSize = 10;

     const [accordion, setAccordion] = useState('');
     const [page, setPage] = useState(1);
     const [totalPages, setTotalPages] = useState(0);
     const [loading, setLoading] = useState(false);
     const [reviewTriggers, setReviewTriggers] = useState(null);
     const [triggerStates, setTriggerStates] = useState({});
     const [showLogModal, setShowLogModal] = useState(false);
     const [selectedLog, setSelectedLog] = useState(null);

     // Combined state for rerun and preview
     const [summaryData, setSummaryData] = useState({
          rerun: {
               requests: [],
               loading: false,
               page: 1,
               totalPages: 0,
          },
          preview: {
               requests: [],
               loading: false,
               page: 1,
               totalPages: 0,
          },
     });

     useEffect(() => {
          if (insight?.runAt && ruleId) {
               fetchReviewServerSideTriggers(page);
          } else {
               setReviewTriggers(null);
          }
          // eslint-disable-next-line
     }, [insight?.runAt, ruleId, accountId, page]);

     useEffect(() => {
          if (insight?.runAt && ruleId) {
               fetchSummaryData('rerun', summaryData.rerun.page);
          } else {
               setSummaryData((prev) => ({ ...prev, rerun: { ...prev.rerun, requests: [] } }));
          }
          // eslint-disable-next-line
     }, [insight?.runAt, ruleId, accountId, summaryData.rerun.page]);

     useEffect(() => {
          if (insight?.runAt && ruleId) {
               fetchSummaryData('preview', summaryData.preview.page);
          } else {
               setSummaryData((prev) => ({ ...prev, preview: { ...prev.preview, requests: [] } }));
          }
          // eslint-disable-next-line
     }, [insight?.runAt, ruleId, accountId, summaryData.preview.page]);

     const fetchReviewServerSideTriggers = async (currentPage = 1) => {
          setLoading(true);
          try {
               const { startDate, endDate } = getDateRangeForDay(insight?.runAt);

               const response = await callTokenApi(
                    `/client/intent-rules/review/${ruleId}/server-side-triggers?accountId=${accountId}&startDate=${encodeURIComponent(
                         startDate
                    )}&endDate=${encodeURIComponent(endDate)}&page=${currentPage}&pageSize=${pageSize}`,
                    'GET'
               );
               if (response?.data?.code === 200 && response.data.data) {
                    setReviewTriggers(response.data.data);
                    setTotalPages(response.data.pagination?.totalPages || 1);
               } else {
                    setReviewTriggers(null);
                    toast.error('Failed to load insight details');
               }
          } catch (error) {
               setReviewTriggers(null);
               toast.error('An error occurred while loading insight details');
          } finally {
               setLoading(false);
          }
     };

     const fetchReviewTriggerDetail = async (triggerId, currentPage = 1) => {
          if (!triggerId || !insight?.runAt) return;

          setTriggerStates((prev) => ({ ...prev, [triggerId]: { ...prev[triggerId], loading: true } }));

          try {
               const { startDate, endDate } = getDateRangeForDay(insight?.runAt);

               const response = await callTokenApi(
                    `/client/intent-rules/review/${ruleId}?accountId=${accountId}&triggerId=${triggerId}&startDate=${encodeURIComponent(
                         startDate
                    )}&endDate=${encodeURIComponent(endDate)}&page=${currentPage}&pageSize=${pageSize}`,
                    'GET'
               );
               if (response?.data?.code === 200 && response.data.data) {
                    setTriggerStates((prev) => ({
                         ...prev,
                         [triggerId]: {
                              loading: false,
                              page: currentPage,
                              totalPages: response.data.pagination?.totalPages || 1,
                              data: response.data.data,
                         },
                    }));
               } else {
                    setTriggerStates((prev) => ({ ...prev, [triggerId]: { loading: false, page: currentPage, totalPages: 1, data: [] } }));
                    toast.error('Failed to load trigger details');
               }
          } catch (error) {
               setTriggerStates((prev) => ({ ...prev, [triggerId]: { loading: false, page: currentPage, totalPages: 1, data: [] } }));
               toast.error('An error occurred while loading trigger details');
          }
     };

     const fetchSummaryData = async (type, currentPage = 1) => {
          const triggerTypeMap = {
               rerun: 'rerun_intent',
               preview: 'manual_preview',
          };

          const triggerType = triggerTypeMap[type];
          if (!triggerType) return;

          setSummaryData((prev) => ({ ...prev, [type]: { ...prev[type], loading: true } }));
          try {
               const { startDate, endDate } = getDateRangeForDay(insight?.runAt);

               const response = await callTokenApi(
                    `/client/intent-rules/review/${ruleId}?accountId=${accountId}&triggerType=${triggerType}&startDate=${encodeURIComponent(
                         startDate
                    )}&endDate=${encodeURIComponent(endDate)}&page=${currentPage}&pageSize=${pageSize}`,
                    'GET'
               );
               if (response?.data?.code === 200 && response.data.data) {
                    setSummaryData((prev) => ({
                         ...prev,
                         [type]: {
                              requests: response.data.data,
                              loading: false,
                              page: currentPage,
                              totalPages: response.data.pagination?.totalPages || 1,
                         },
                    }));
               } else {
                    setSummaryData((prev) => ({ ...prev, [type]: { ...prev[type], requests: [], loading: false } }));
                    toast.error(`Failed to load ${type} details`);
               }
          } catch (error) {
               setSummaryData((prev) => ({ ...prev, [type]: { ...prev[type], requests: [], loading: false } }));
               toast.error(`An error occurred while loading ${type} details`);
          }
     };

     const getDateRangeForDay = (runAt) => {
          const date = dayjs(runAt).utc();
          const startDate = date.startOf('day').toISOString(); // 00:00:00.000Z
          const endDate = date.endOf('day').toISOString(); // 23:59:59.999Z
          return { startDate, endDate };
     };

     const formatDate = (dateString) => {
          if (!dateString) return 'N/A';
          return dayjs.utc(dateString).local().format('MMM D, YYYY');
     };

     const handleAccordionToggle = (triggerId) => {
          setAccordion((prev) => (prev === triggerId ? '' : triggerId));

          if (accordion !== triggerId && !triggerStates[triggerId]?.data) {
               fetchReviewTriggerDetail(triggerId, 1);
          }
     };

     const handlePageChange = (triggerId, newPage) => {
          fetchReviewTriggerDetail(triggerId, newPage);
     };

     const handleOpenLog = (request) => {
          setSelectedLog(request);
          setShowLogModal(true);
     };

     const handleCloseLog = () => {
          setShowLogModal(false);
          setSelectedLog(null);
     };

     const displayData = reviewTriggers || [];
     const { ruleName } = displayData[0] || summaryData.rerun.requests[0] || summaryData.preview.requests[0] || {};

     return (
          <div className='rule-detail account-connected-detail review-analyse'>
               <div className='cvr-create-new'>
                    <h1 className='mb-3'>AI Intent Analysis</h1>
                    {!loading && ruleName && (
                         <ul className='d-flex flex-column gap-1 list-unstyled my-4'>
                              <li className='d-flex gap-2'>
                                   <strong>Intent Rule:</strong>
                                   <span>{ruleName || 'N/A'}</span>
                              </li>
                              <li className='d-flex gap-2'>
                                   <strong>Generated on:</strong>
                                   <span>{formatDate(insight?.runAt)}</span>
                              </li>
                              <li className='d-flex gap-2'>
                                   <strong>Completed on:</strong>
                                   <span>{formatDate(insight?.runAt)}</span>
                              </li>
                         </ul>
                    )}
                    {loading ? (
                         <div className='text-center py-5'>
                              <CSpinner color='primary' />
                              <p className='mt-3'>Loading insight details...</p>
                         </div>
                    ) : !displayData ? (
                         <div className='text-center py-5'>
                              <p>No insight data available.</p>
                         </div>
                    ) : (
                         <>
                              <div className='insight-content review-triggers'>
                                   <div className='position-relative accordion'>
                                        <h4 className='mb-4'>Server Side Trigger Summary</h4>
                                        {displayData.map((item) => {
                                             const triggerState = triggerStates[item.triggerId] || {};
                                             const isLoading = triggerState.loading || false;
                                             const triggerRequests = triggerState.data || [];
                                             const triggerPage = triggerState.page || 1;
                                             const triggerTotalPages = triggerState.totalPages || 1;

                                             return (
                                                  <Accordion
                                                       key={item.triggerId}
                                                       accordionType={item.triggerId}
                                                       title={
                                                            <div className='d-flex align-items-center w-100'>
                                                                 <span>{item.triggerName}</span>
                                                                 <div className='d-flex gap-2 small ms-auto align-items-center'>
                                                                      <span>{item.matchedCount} matched</span>
                                                                      <span>|</span>
                                                                      <span className='text-success'>{item.successCount} success</span>
                                                                      <span>|</span>
                                                                      <span className='text-danger'>{item.failedCount} failed</span>
                                                                 </div>
                                                            </div>
                                                       }
                                                       accordionShow={accordion}
                                                       setAccordionShow={handleAccordionToggle}
                                                  >
                                                       <CRow className='gap-3'>
                                                            <CCol lg='12'>
                                                                 {isLoading ? (
                                                                      <div className='text-center py-3'>
                                                                           <CSpinner color='primary' />
                                                                      </div>
                                                                 ) : triggerRequests.length > 0 ? (
                                                                      <RequestWrapper
                                                                           requests={triggerRequests}
                                                                           models={models}
                                                                           timeZone={timeZone}
                                                                           handleOpenLog={handleOpenLog}
                                                                           currentPage={triggerPage}
                                                                           totalPages={triggerTotalPages}
                                                                           onPageChange={(newPage) => handlePageChange(item.triggerId, newPage)}
                                                                      />
                                                                 ) : (
                                                                      <p className='text-center mb-0'>No data available</p>
                                                                 )}
                                                            </CCol>
                                                       </CRow>
                                                  </Accordion>
                                             );
                                        })}
                                   </div>

                                   {totalPages > 1 && (
                                        <div className='d-flex justify-content-center mt-4'>
                                             <CSmartPagination activePage={page} pages={totalPages} onActivePageChange={(newPage) => setPage(newPage)} />
                                        </div>
                                   )}
                              </div>
                         </>
                    )}

                    {/* Re-run Summary Component */}
                    <div className='mt-4'>
                         <hr />
                         <h4 className='mb-4'>Re-run Summary</h4>
                         {summaryData.rerun.loading ? (
                              <div className='text-center py-3'>
                                   <CSpinner color='primary' />
                                   <p className='mt-3'>Loading re-run data...</p>
                              </div>
                         ) : summaryData.rerun.requests.length > 0 ? (
                              <RequestWrapper
                                   requests={summaryData.rerun.requests}
                                   models={models}
                                   timeZone={timeZone}
                                   handleOpenLog={handleOpenLog}
                                   currentPage={summaryData.rerun.page}
                                   totalPages={summaryData.rerun.totalPages}
                                   onPageChange={(newPage) => setSummaryData((prev) => ({ ...prev, rerun: { ...prev.rerun, page: newPage } }))}
                              />
                         ) : (
                              <p className='text-center mb-0'>No re-run data available.</p>
                         )}
                    </div>

                    {/* Preview Summary Component */}
                    <div className='mt-4'>
                         <hr />
                         <h4 className='mb-4'>Preview Summary</h4>
                         {summaryData.preview.loading ? (
                              <div className='text-center py-3'>
                                   <CSpinner color='primary' />
                                   <p className='mt-3'>Loading preview data...</p>
                              </div>
                         ) : summaryData.preview.requests.length > 0 ? (
                              <RequestWrapper
                                   requests={summaryData.preview.requests}
                                   models={models}
                                   timeZone={timeZone}
                                   handleOpenLog={handleOpenLog}
                                   currentPage={summaryData.preview.page}
                                   totalPages={summaryData.preview.totalPages}
                                   onPageChange={(newPage) => setSummaryData((prev) => ({ ...prev, preview: { ...prev.preview, page: newPage } }))}
                              />
                         ) : (
                              <p className='text-center mb-0'>No preview data available.</p>
                         )}
                    </div>
               </div>

               <div className='intent-log-modal'>
                    <CModal visible={showLogModal} onClose={handleCloseLog} scrollable alignment='center' portal={false} size='lg'>
                         <CModalHeader>
                              <CModalTitle>Analysis Result</CModalTitle>
                         </CModalHeader>
                         <CModalBody>
                              {selectedLog && (
                                   <div>
                                        <div className='mb-3'>
                                             <strong>Record ID:</strong> {selectedLog.objectId || 'N/A'}
                                        </div>
                                        <div className='mb-3'>
                                             <strong>Run time:</strong> {formatTimeRunAt(selectedLog.created_at, timeZone)}
                                        </div>
                                        <div className='mb-3'>
                                             <strong>Model:</strong> {(models || []).find((model) => model.id === selectedLog.llmModelId)?.displayName || ''}
                                        </div>
                                        <div className='mb-3 d-flex align-items-center gap-2'>
                                             <strong>Type:</strong> {getTooltipTag(selectedLog.triggerType)}
                                        </div>
                                        <div className='mb-3 d-flex align-items-center gap-2'>
                                             <strong>Status:</strong> {renderStatusBadge(selectedLog.status)}
                                        </div>
                                        <hr />
                                        <ViewMarkdownReview markdown={selectedLog.result || 'No result available'} />
                                   </div>
                              )}
                         </CModalBody>
                    </CModal>
               </div>
          </div>
     );
};

export default ReviewPrompt;
